import { useState } from 'react'
import {
  ReferenceInput,
  AutocompleteInput,
  NumberInput,
  useDataProvider,
  useNotify,
  useRefresh,
  Form
} from 'react-admin'
import { Box, Stack, Typography, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material'

import { CommonDialog } from '../../common/Dialog'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

const PaieEventDialog = ({ open, setOpen }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  const [profileId, setProfileId] = useState(null)
  const [missionId, setMissionId] = useState(null)
  const [unitId, setUnitId] = useState(null)
  const [amount, setAmount] = useState('')
  const [quantity, setQuantity] = useState('')
  const [startMonth, setStartMonth] = useState('')
  const [endMonth, setEndMonth] = useState('')

  // Generate list of months (current month and next 12 months)
  const generateMonthOptions = () => {
    const months = []
    const today = new Date()
    
    for (let i = 0; i < 13; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1)
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const label = date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
      months.push({ value, label })
    }
    
    return months
  }

  const monthOptions = generateMonthOptions()

  const handleSubmit = async () => {
    // Validation
    if (!profileId || !missionId || !unitId || !amount || !quantity || !startMonth || !endMonth) {
      notify('Veuillez remplir tous les champs', { type: 'warning' })
      return
    }

    const start = new Date(startMonth + '-01')
    const end = new Date(endMonth + '-01')

    if (start > end) {
      notify('Le mois de début doit être avant ou égal au mois de fin', { type: 'warning' })
      return
    }

    // Calculate number of months
    const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
    
    // Calculate amount per month
    const totalAmount = parseFloat(amount) * parseFloat(quantity)
    const amountPerMonth = totalAmount / monthsDiff

    try {
      // Create paie lines for each month
      const promises = []
      
      for (let i = 0; i < monthsDiff; i++) {
        const currentMonth = new Date(start.getFullYear(), start.getMonth() + i, 15) // 15th of each month
        
        promises.push(
          dataProvider.create('paie_pubeur', {
            data: {
              profile_id: profileId,
              event_id: null, // No event for forfait
              mission_id: missionId,
              unit_id: unitId,
              quantity: 1, // Fixed quantity of 1 for forfait
              amount: amountPerMonth,
              created_at: currentMonth.toISOString().split('T')[0],
            }
          })
        )
      }

      await Promise.all(promises)

      notify(`${monthsDiff} ligne(s) de paie créée(s) avec succès`, { type: 'success' })
      
      // Reset form
      resetForm()
      
      setOpen(false)
      refresh()
    } catch (error) {
      console.error('Error creating paie lines:', error)
      notify('Erreur lors de la création des lignes de paie', { type: 'error' })
    }
  }

  const resetForm = () => {
    setProfileId(null)
    setMissionId(null)
    setUnitId(null)
    setAmount('')
    setQuantity('')
    setStartMonth('')
    setEndMonth('')
  }

  const handleClose = () => {
    resetForm()
    setOpen(false)
  }

  const totalAmount = amount && quantity ? 
    (parseFloat(amount) * parseFloat(quantity)).toFixed(2) : '0.00'
  
  const monthsDiff = startMonth && endMonth ? 
    (() => {
      const start = new Date(startMonth + '-01')
      const end = new Date(endMonth + '-01')
      return start <= end ? (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1 : 0
    })() : 0
  
  const amountPerMonth = monthsDiff > 0 ? (parseFloat(totalAmount) / monthsDiff).toFixed(2) : '0.00'

  const isFormValid = profileId && missionId && unitId && 
                      amount && quantity && startMonth && endMonth

  return (
    <CommonDialog
      open={open}
      handleClose={handleClose}
      title="Ajouter un forfait de paie"
      icon={<AddCircleOutlineIcon />}
      size="md"
      minHeight="50px"
    >
      <Box>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Ce menu vous permet d'assigner un forfait à un pubeur, à répartir sur plusieurs mois de paie.
        </Typography>

        <Form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Profile Selection */}
            <ReferenceInput source="profile_id" reference="profiles" filter={{ enabled: true }}>
              <AutocompleteInput
                optionText={(record) => `${record.first_name} ${record.last_name}`}
                label="Pubeur"
                filterToQuery={(searchText) => ({ 'full_name@ilike': searchText })}
                onChange={(value) => setProfileId(value)}
                fullWidth
              />
            </ReferenceInput>

            {/* Mission and Unit Selection */}
            <Stack direction="row" spacing={2}>
              <ReferenceInput source="mission_id" reference="paie_missions">
                <AutocompleteInput
                  optionText="name"
                  label="Mission"
                  sx={{ width: '60%' }}
                  filterToQuery={(searchText) => ({ 'name@ilike': searchText })}
                  onChange={(value) => setMissionId(value)}
                />
              </ReferenceInput>

              <ReferenceInput source="unit_id" reference="paie_units">
                <AutocompleteInput
                  optionText="name"
                  label="Unité de mesure"
                  sx={{ width: '40%' }}
                  filterToQuery={(searchText) => ({ 'name@ilike': searchText })}
                  onChange={(value) => setUnitId(value)}
                />
              </ReferenceInput>
            </Stack>

            {/* Amount and Quantity */}
            <Stack direction="row" spacing={2}>
              <NumberInput
                source="amount"
                label="Montant unitaire"
                onChange={(e) => setAmount(e.target.value)}
                sx={{ width: '50%' }}
              />

              <NumberInput
                source="quantity"
                label="Quantité"
                onChange={(e) => setQuantity(e.target.value)}
                sx={{ width: '50%' }}
              />
            </Stack>

            {/* Total Amount Display */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                Montant total: {totalAmount} €
              </Typography>
            </Box>

            {/* Month Range Selection */}
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ width: '50%' }}>
                <InputLabel>Mois de début</InputLabel>
                <Select
                  value={startMonth}
                  onChange={(e) => setStartMonth(e.target.value)}
                  label="Mois de début"
                >
                  {monthOptions.map(month => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: '50%' }}>
                <InputLabel>Mois de fin</InputLabel>
                <Select
                  value={endMonth}
                  onChange={(e) => setEndMonth(e.target.value)}
                  label="Mois de fin"
                >
                  {monthOptions.map(month => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Calculation Summary */}
            {monthsDiff > 0 && (
              <Box sx={{ p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{monthsDiff} mois</strong> sélectionné(s)
                </Typography>

                <Typography variant="body2">
                  Montant par mois: <strong>{amountPerMonth} €</strong>
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={handleClose}>
                Annuler
              </Button>

              <Button 
                type="submit"
                variant="contained"
                disabled={!isFormValid}
              >
                Créer les lignes de paie
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Box>
    </CommonDialog>
  )
}

export default PaieEventDialog